import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Users, UserCheck, Clock, Calendar, Shield, AlertTriangle,
  Plus, Search, RefreshCw, Eye, Edit, Award, Plane,
  Activity, Timer, ChevronRight, Loader2, FileText, CheckCircle2,
  AlertCircle, User, MapPin, Mail, Phone, Briefcase, Heart,
  CreditCard, Globe, BookOpen, Languages, Stethoscope, Building,
  Home, UserCog, ChevronLeft, Save, Link2, TrendingUp, BarChart3,
  Brain, Sparkles, Zap, LineChart
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function CrewManagementPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Crew members
  const [crewMembers, setCrewMembers] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [showCrewDialog, setShowCrewDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [crewFormStep, setCrewFormStep] = useState(1);
  const [crewForm, setCrewForm] = useState({
    // Basic Info (Step 1)
    employee_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'first_officer',
    base_airport: 'OEJN',
    // Personal Details (Step 2)
    date_of_birth: '',
    nationality: '',
    gender: '',
    // Passport & Visa (Step 3)
    passport_number: '',
    passport_expiry: '',
    passport_issuing_country: '',
    visa_type: '',
    visa_number: '',
    visa_expiry: '',
    visa_countries: [],
    // Employment (Step 4)
    hire_date: '',
    seniority_number: '',
    employment_type: 'full_time',
    contract_end_date: '',
    fleet_assignment: '',
    // License & Medical (Step 5)
    license_type: 'none',
    license_number: '',
    license_issuing_authority: 'GACA',
    license_expiry: '',
    medical_class: '',
    medical_expiry: '',
    medical_restrictions: '',
    // Contact & Emergency (Step 6)
    address_line1: '',
    address_line2: '',
    city: '',
    country: '',
    postal_code: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    // Operational (Step 7)
    languages_spoken: [],
    aircraft_type_ratings: [],
    special_qualifications: [],
    notes: ''
  });
  
  // Qualifications
  const [qualifications, setQualifications] = useState([]);
  const [showQualDialog, setShowQualDialog] = useState(false);
  const [qualForm, setQualForm] = useState({
    crew_member_id: '',
    qualification_type: 'license',
    name: '',
    number: '',
    issued_by: 'GACA',
    issue_date: '',
    expiry_date: ''
  });
  
  // Schedules
  const [schedules, setSchedules] = useState([]);
  
  // Crew Pairing
  const [pairingDashboard, setPairingDashboard] = useState(null);
  const [loadingPairings, setLoadingPairings] = useState(false);
  
  // AI Fatigue Prediction
  const [fatiguePredictions, setFatiguePredictions] = useState(null);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [showPredictionDialog, setShowPredictionDialog] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // ==================== DATA FETCHING ====================

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crew/stats`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchCrewMembers = async () => {
    try {
      let url = `${API_URL}/api/crew/members?limit=100`;
      if (searchQuery) url += `&search=${searchQuery}`;
      if (roleFilter && roleFilter !== 'all') url += `&role=${roleFilter}`;
      
      const res = await fetch(url, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setCrewMembers(data.crew_members || []);
      }
    } catch (err) {
      console.error('Error fetching crew:', err);
    }
  };

  const fetchQualifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crew/qualifications?limit=200`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setQualifications(data.qualifications || []);
      }
    } catch (err) {
      console.error('Error fetching qualifications:', err);
    }
  };

  const fetchSchedules = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/api/crew/schedules?start_date=${today}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setSchedules(data.schedules || []);
      }
    } catch (err) {
      console.error('Error fetching schedules:', err);
    }
  };

  const fetchCrewDetail = async (crewId) => {
    try {
      const res = await fetch(`${API_URL}/api/crew/members/${crewId}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setSelectedCrew(data);
        setShowDetailDialog(true);
      }
    } catch (err) {
      console.error('Error fetching crew detail:', err);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchCrewMembers(),
      fetchQualifications(),
      fetchSchedules(),
      fetchPairingDashboard(),
      fetchFatiguePredictions()
    ]);
    setLoading(false);
  };

  const fetchPairingDashboard = async () => {
    setLoadingPairings(true);
    try {
      const res = await fetch(`${API_URL}/api/crew/pairings/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setPairingDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching pairing dashboard:', err);
    } finally {
      setLoadingPairings(false);
    }
  };

  const fetchFatiguePredictions = async () => {
    setLoadingPredictions(true);
    try {
      const res = await fetch(`${API_URL}/api/crew/fatigue-prediction/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setFatiguePredictions(data);
      }
    } catch (err) {
      console.error('Error fetching fatigue predictions:', err);
    } finally {
      setLoadingPredictions(false);
    }
  };

  const runFatigueAnalysis = async () => {
    setLoadingPredictions(true);
    try {
      const res = await fetch(`${API_URL}/api/crew/fatigue-prediction/analyze-all`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Analyzed ${data.analyzed_count} crew members`);
        fetchFatiguePredictions();
      }
    } catch (err) {
      toast.error('Error running fatigue analysis');
    } finally {
      setLoadingPredictions(false);
    }
  };

  const viewCrewPrediction = async (crewId) => {
    try {
      const res = await fetch(`${API_URL}/api/crew/fatigue-prediction/${crewId}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setSelectedPrediction(data);
        setShowPredictionDialog(true);
      }
    } catch (err) {
      toast.error('Error fetching prediction details');
    }
  };

  const seedPairingData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crew/pairings/seed-sample`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchPairingDashboard();
      }
    } catch (err) {
      toast.error('Error seeding pairing data');
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchCrewMembers();
  }, [searchQuery, roleFilter]);

  // ==================== ACTIONS ====================

  const createCrewMember = async () => {
    try {
      // Prepare form data with proper formatting
      const formData = {
        ...crewForm,
        seniority_number: crewForm.seniority_number ? parseInt(crewForm.seniority_number) : null,
        languages_spoken: crewForm.languages_spoken || [],
        aircraft_type_ratings: crewForm.aircraft_type_ratings || [],
        special_qualifications: crewForm.special_qualifications || [],
        visa_countries: crewForm.visa_countries || []
      };
      
      const res = await fetch(`${API_URL}/api/crew/members`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success('Crew member created successfully');
        setShowCrewDialog(false);
        resetCrewForm();
        fetchCrewMembers();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create crew member');
      }
    } catch (err) {
      toast.error('Error creating crew member');
    }
  };

  const resetCrewForm = () => {
    setCrewForm({
      employee_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: 'first_officer',
      base_airport: 'OEJN',
      date_of_birth: '',
      nationality: '',
      gender: '',
      passport_number: '',
      passport_expiry: '',
      passport_issuing_country: '',
      visa_type: '',
      visa_number: '',
      visa_expiry: '',
      visa_countries: [],
      hire_date: '',
      seniority_number: '',
      employment_type: 'full_time',
      contract_end_date: '',
      fleet_assignment: '',
      license_type: 'none',
      license_number: '',
      license_issuing_authority: 'GACA',
      license_expiry: '',
      medical_class: '',
      medical_expiry: '',
      medical_restrictions: '',
      address_line1: '',
      address_line2: '',
      city: '',
      country: '',
      postal_code: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      languages_spoken: [],
      aircraft_type_ratings: [],
      special_qualifications: [],
      notes: ''
    });
    setCrewFormStep(1);
  };

  const createQualification = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crew/qualifications`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(qualForm)
      });
      if (res.ok) {
        toast.success('Qualification added');
        setShowQualDialog(false);
        setQualForm({
          crew_member_id: '',
          qualification_type: 'license',
          name: '',
          number: '',
          issued_by: 'GACA',
          issue_date: '',
          expiry_date: ''
        });
        fetchQualifications();
        fetchStats();
      } else {
        toast.error('Failed to add qualification');
      }
    } catch (err) {
      toast.error('Error adding qualification');
    }
  };

  const checkFTL = async (crewId) => {
    try {
      const res = await fetch(`${API_URL}/api/crew/ftl-check/${crewId}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        if (data.is_compliant) {
          toast.success(`FTL Compliant: ${data.remaining.flight_time_7_days}h remaining in 7-day limit`);
        } else {
          toast.error(`FTL Violation: ${data.violations.map(v => v.limit).join(', ')}`);
        }
      }
    } catch (err) {
      toast.error('Error checking FTL');
    }
  };

  const seedSampleData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crew/seed-sample-data`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchAll();
      }
    } catch (err) {
      toast.error('Error seeding data');
    }
  };

  // ==================== HELPERS ====================

  const getRoleBadge = (role) => {
    const colors = {
      captain: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      first_officer: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      purser: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
      cabin_crew: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      instructor: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      examiner: 'bg-orange-500/20 text-orange-300 border-orange-500/30'
    };
    return colors[role] || 'bg-slate-500/20 text-slate-300';
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-500/20 text-green-300',
      on_leave: 'bg-yellow-500/20 text-yellow-300',
      sick: 'bg-orange-500/20 text-orange-300',
      training: 'bg-blue-500/20 text-blue-300',
      suspended: 'bg-red-500/20 text-red-300',
      inactive: 'bg-slate-500/20 text-slate-300'
    };
    return colors[status] || 'bg-slate-500/20 text-slate-300';
  };

  const getQualStatusBadge = (status, daysLeft) => {
    if (status === 'expired') return 'bg-red-500/20 text-red-300';
    if (status === 'expiring_soon' || (daysLeft !== null && daysLeft <= 30)) return 'bg-yellow-500/20 text-yellow-300';
    return 'bg-green-500/20 text-green-300';
  };

  const getFatigueColor = (level) => {
    const colors = {
      low: 'text-green-400',
      moderate: 'text-yellow-400',
      high: 'text-orange-400',
      critical: 'text-red-400'
    };
    return colors[level] || 'text-slate-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        <span className="ml-3 text-slate-400">{t('crew.loadingCrewData')}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="crew-management-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">{t('crew.title')}</h1>
            <p className="text-slate-400">{t('crew.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchAll} className="border-slate-600">
            <RefreshCw className="w-4 h-4 mr-2" /> {t('common.refresh')}
          </Button>
          <Button onClick={seedSampleData} className="bg-purple-500 hover:bg-purple-600">
            <Users className="w-4 h-4 mr-2" /> {t('common.seedData')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{t('crew.totalCrew')}</p>
                  <p className="text-2xl font-bold text-white">{stats.crew.total}</p>
                </div>
                <Users className="w-8 h-8 text-blue-400 opacity-70" />
              </div>
              <p className="text-xs text-slate-500 mt-1">{stats.crew.active} active</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{t('crew.onDuty')}</p>
                  <p className="text-2xl font-bold text-green-400">{stats.crew.on_duty}</p>
                </div>
                <Activity className="w-8 h-8 text-green-400 opacity-70" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{t('crew.captain')}</p>
                  <p className="text-2xl font-bold text-purple-400">{stats.by_role.captains}</p>
                </div>
                <Award className="w-8 h-8 text-purple-400 opacity-70" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{t('crew.validQuals')}</p>
                  <p className="text-2xl font-bold text-white">{stats.qualifications.valid}</p>
                </div>
                <Shield className="w-8 h-8 text-cyan-400 opacity-70" />
              </div>
              <p className="text-xs text-yellow-400 mt-1">{stats.qualifications.expiring_30_days} expiring</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{t('crew.dutiesToday')}</p>
                  <p className="text-2xl font-bold text-orange-400">{stats.schedule.duties_today}</p>
                </div>
                <Calendar className="w-8 h-8 text-orange-400 opacity-70" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{t('crew.lowFatigue')}</p>
                  <p className="text-2xl font-bold text-emerald-400">{stats.fatigue.low}</p>
                </div>
                <Timer className="w-8 h-8 text-emerald-400 opacity-70" />
              </div>
              {stats.fatigue.high > 0 && (
                <p className="text-xs text-red-400 mt-1">{stats.fatigue.high} high risk</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 mb-6">
          <TabsTrigger value="dashboard" data-testid="crew-dashboard-tab">
            <Activity className="w-4 h-4 mr-2" /> {t('common.dashboard')}
          </TabsTrigger>
          <TabsTrigger value="crew" data-testid="crew-list-tab">
            <Users className="w-4 h-4 mr-2" /> {t('crew.crewMembers')}
          </TabsTrigger>
          <TabsTrigger value="qualifications" data-testid="qualifications-tab">
            <Award className="w-4 h-4 mr-2" /> {t('crew.qualifications')}
          </TabsTrigger>
          <TabsTrigger value="schedules" data-testid="schedules-tab">
            <Calendar className="w-4 h-4 mr-2" /> {t('crew.schedules')}
          </TabsTrigger>
          <TabsTrigger value="ftl" data-testid="ftl-tab">
            <Clock className="w-4 h-4 mr-2" /> {t('crew.ftlCompliance')}
          </TabsTrigger>
          <TabsTrigger value="pairings" data-testid="pairings-tab">
            <Link2 className="w-4 h-4 mr-2" /> {t('crew.crewPairing')}
          </TabsTrigger>
          <TabsTrigger value="fatigue-ai" data-testid="fatigue-ai-tab">
            <Brain className="w-4 h-4 mr-2" /> {t('crew.aiFatiguePrediction')}
          </TabsTrigger>
        </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Crew by Role */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  {t('crew.crewOverview')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <Award className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-white">{t('crew.captain')}</span>
                    </div>
                    <span className="text-2xl font-bold text-purple-400">{stats?.by_role.captains || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-white">{t('crew.firstOfficer')}</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-400">{stats?.by_role.first_officers || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-cyan-400" />
                      </div>
                      <span className="text-white">{t('crew.cabinCrew')}</span>
                    </div>
                    <span className="text-2xl font-bold text-cyan-400">{stats?.by_role.cabin_crew || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expiring Qualifications */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  {t('crew.expiringQualifications')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {qualifications
                    .filter(q => q.status === 'expiring_soon' || (q.days_until_expiry !== null && q.days_until_expiry <= 30))
                    .slice(0, 5)
                    .map(qual => (
                      <div key={qual.id} className="flex items-center justify-between p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <div>
                          <p className="font-medium text-white">{qual.name}</p>
                          <p className="text-sm text-slate-400">{qual.qualification_type?.replace('_', ' ')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-yellow-400 font-medium">{qual.days_until_expiry} days</p>
                          <p className="text-xs text-slate-500">{qual.expiry_date?.slice(0, 10)}</p>
                        </div>
                      </div>
                    ))}
                  {qualifications.filter(q => q.days_until_expiry <= 30).length === 0 && (
                    <p className="text-slate-500 text-center py-4">{t('crew.noExpiringQualifications')}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-400" />
                  {t('common.quickActions')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                    onClick={() => setShowCrewDialog(true)}
                  >
                    <Plus className="w-5 h-5 text-blue-400" />
                    <span className="text-sm">{t('crew.addCrewMember')}</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                    onClick={() => setShowQualDialog(true)}
                  >
                    <Award className="w-5 h-5 text-cyan-400" />
                    <span className="text-sm">{t('crew.addQualification')}</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                    onClick={() => setActiveTab('schedules')}
                  >
                    <Calendar className="w-5 h-5 text-orange-400" />
                    <span className="text-sm">{t('crew.viewSchedule')}</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                    onClick={() => setActiveTab('ftl')}
                  >
                    <Clock className="w-5 h-5 text-purple-400" />
                    <span className="text-sm">{t('crew.ftlCheck')}</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* CREW MEMBERS TAB */}
        <TabsContent value="crew">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('crew.crewMembers')}</CardTitle>
                <CardDescription>{t('crew.manageFlightCabinCrew')}</CardDescription>
              </div>
              <Button onClick={() => setShowCrewDialog(true)} className="bg-blue-500 hover:bg-blue-600">
                <Plus className="w-4 h-4 mr-2" /> {t('crew.addCrewMember')}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder={t('crew.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-900 border-slate-600"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                    <SelectValue placeholder={t('crew.allRoles')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('crew.allRoles')}</SelectItem>
                    <SelectItem value="captain">{t('crew.captain')}</SelectItem>
                    <SelectItem value="first_officer">{t('crew.firstOfficer')}</SelectItem>
                    <SelectItem value="purser">{t('crew.purser')}</SelectItem>
                    <SelectItem value="cabin_crew">{t('crew.cabinCrew')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                {crewMembers.map(crew => (
                  <div key={crew.id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center">
                          <User className="w-6 h-6 text-slate-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-white">{crew.full_name || `${crew.first_name} ${crew.last_name}`}</h3>
                            <Badge className={getRoleBadge(crew.role)}>{crew.role?.replace('_', ' ')}</Badge>
                            <Badge className={getStatusBadge(crew.status)}>{crew.status}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3" /> {crew.employee_id}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {crew.base_airport}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {crew.email}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-4">
                          <p className={`text-sm ${getFatigueColor(crew.fatigue_risk_level)}`}>
                            {t('common.fatigue')}: {crew.fatigue_risk_level || 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {crew.qualifications_valid || 0} {t('crew.qualsValid')}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => checkFTL(crew.id)}>
                          <Clock className="w-4 h-4 mr-1" /> {t('common.ftl')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => fetchCrewDetail(crew.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {crewMembers.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('crew.noCrewFound')}</p>
                    <p className="text-sm">{t('crew.addCrewOrSeed')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* QUALIFICATIONS TAB */}
        <TabsContent value="qualifications">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('crew.qualificationsCertifications')}</CardTitle>
                <CardDescription>{t('crew.trackLicensesTypeRatings')}</CardDescription>
              </div>
              <Button onClick={() => setShowQualDialog(true)} className="bg-cyan-500 hover:bg-cyan-600">
                <Plus className="w-4 h-4 mr-2" /> {t('crew.addQualification')}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {qualifications.map(qual => (
                  <div key={qual.id} className={`p-4 rounded-lg border transition-colors ${
                    qual.status === 'expired' ? 'bg-red-500/10 border-red-500/30' :
                    qual.days_until_expiry <= 30 ? 'bg-yellow-500/10 border-yellow-500/30' :
                    'bg-slate-900/50 border-slate-700'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-white">{qual.name}</span>
                          <Badge className="bg-slate-500/20 text-slate-300">{qual.qualification_type?.replace('_', ' ')}</Badge>
                          <Badge className={getQualStatusBadge(qual.status, qual.days_until_expiry)}>
                            {qual.status}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-400">
                          {qual.number && <span>#{qual.number} • </span>}
                          <span>Issued by: {qual.issued_by || 'N/A'}</span>
                          {qual.aircraft_type && <span> • Type: {qual.aircraft_type}</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        {qual.expiry_date && (
                          <>
                            <p className={`font-medium ${
                              qual.days_until_expiry <= 0 ? 'text-red-400' :
                              qual.days_until_expiry <= 30 ? 'text-yellow-400' :
                              'text-green-400'
                            }`}>
                              {qual.days_until_expiry <= 0 ? 'Expired' : `${qual.days_until_expiry} days`}
                            </p>
                            <p className="text-xs text-slate-500">Expires: {qual.expiry_date.slice(0, 10)}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {qualifications.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('crew.noQualificationsFound')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SCHEDULES TAB */}
        <TabsContent value="schedules">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle>{t('crew.schedules')}</CardTitle>
              <CardDescription>{t('crew.viewManageRosters')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {schedules.map(schedule => (
                  <div key={schedule.id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white">{schedule.crew_name}</p>
                        <p className="text-sm text-slate-400">
                          {schedule.schedule_date} • {schedule.duty_type?.replace('_', ' ')}
                        </p>
                        {schedule.route && <p className="text-xs text-blue-400">{schedule.route}</p>}
                      </div>
                      <Badge className={schedule.status === 'published' ? 'bg-green-500/20 text-green-300' : 'bg-slate-500/20 text-slate-300'}>
                        {schedule.status}
                      </Badge>
                    </div>
                  </div>
                ))}
                {schedules.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('crew.noUpcomingSchedules')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FTL COMPLIANCE TAB */}
        <TabsContent value="ftl">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-400" />
                {t('crew.ftlComplianceOverview')}
              </CardTitle>
              <CardDescription>{t('crew.ftlMonitoring')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-slate-900/50 rounded-lg">
                  <h3 className="font-medium text-white mb-3">{t('crew.ftlLimits')}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Max Flight Time (7 days)</span>
                      <span className="text-white">60 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Max Flight Time (28 days)</span>
                      <span className="text-white">190 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Max Flight Time (year)</span>
                      <span className="text-white">900 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Max FDP (2 sectors)</span>
                      <span className="text-white">13 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Min Rest (short haul)</span>
                      <span className="text-white">10 hours</span>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-slate-900/50 rounded-lg">
                  <h3 className="font-medium text-white mb-3">{t('crew.complianceStatus')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">{t('crew.fullyCompliant')}</span>
                      <Badge className="bg-green-500/20 text-green-300">{stats?.ftl.compliant || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">{t('common.warnings')}</span>
                      <Badge className="bg-yellow-500/20 text-yellow-300">{stats?.ftl.warnings || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">{t('common.violations')}</span>
                      <Badge className="bg-red-500/20 text-red-300">{stats?.ftl.violations || 0}</Badge>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-slate-500 text-center">
                {t('crew.clickFTLButton')}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CREW PAIRING TAB */}
        <TabsContent value="pairings">
          <div className="space-y-6">
            {/* Pairing Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">{t('crew.uniquePairings')}</p>
                      <p className="text-2xl font-bold text-white">{pairingDashboard?.summary?.total_unique_pairings || 0}</p>
                    </div>
                    <Link2 className="w-8 h-8 text-blue-400 opacity-70" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">{t('common.thisMonth')}</p>
                      <p className="text-2xl font-bold text-white">{pairingDashboard?.summary?.pairings_this_month || 0}</p>
                    </div>
                    <Calendar className="w-8 h-8 text-purple-400 opacity-70" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">{t('crew.frequentPairs')}</p>
                      <p className="text-2xl font-bold text-yellow-400">{pairingDashboard?.summary?.frequent_pairings || 0}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-yellow-400 opacity-70" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t('crew.flightsTogether')}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">{t('crew.fatigueRisk')}</p>
                      <p className="text-2xl font-bold text-red-400">{pairingDashboard?.summary?.fatigue_risk_pairings || 0}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-red-400 opacity-70" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t('crew.needRotation')}</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Pairings */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-400" />
                      {t('crew.topCrewPairings')}
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={seedPairingData}>
                      {t('crew.generateSample')}
                    </Button>
                  </div>
                  <CardDescription>{t('crew.crewsFlyMostFrequently')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pairingDashboard?.top_pairings?.slice(0, 5).map((pairing, idx) => (
                      <div key={idx} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-sm font-bold text-purple-400">
                              #{idx + 1}
                            </div>
                            <div>
                              <p className="text-white font-medium">{pairing.captain_name}</p>
                              <p className="text-sm text-slate-400">+ {pairing.first_officer_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-blue-400">{pairing.total_flights_together}</p>
                            <p className="text-xs text-slate-500">{t('fdm.flights')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-400">
                          <span>{pairing.total_hours_together}h {t('crew.together')}</span>
                          <span>{pairing.long_haul_flights} {t('crew.longHaul')}</span>
                          {pairing.fatigue_risk_flag && (
                            <Badge className="bg-red-500/20 text-red-300 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" /> {t('crew.fatigueRisk')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!pairingDashboard?.top_pairings || pairingDashboard.top_pairings.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        <Link2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t('crew.noPairingData')}</p>
                        <p className="text-xs mt-1">{t('crew.clickGenerateSample')}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Fatigue Alerts & Suggestions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                    {t('crew.fatigueAlertsRecommendations')}
                  </CardTitle>
                  <CardDescription>{t('crew.crewCombinationsNeedingAttention')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Fatigue Alerts */}
                    {pairingDashboard?.fatigue_alerts?.map((alert, idx) => (
                      <div key={idx} className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                            alert.severity === 'high' ? 'text-red-400' : 'text-orange-400'
                          }`} />
                          <div>
                            <p className="font-medium text-white">{alert.captain} + {alert.first_officer}</p>
                            <p className="text-sm text-slate-400 mt-1">{alert.reason}</p>
                            <p className="text-xs text-blue-400 mt-2">{alert.recommendation}</p>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Optimization Suggestions */}
                    {pairingDashboard?.optimization_suggestions?.map((suggestion, idx) => (
                      <div key={idx} className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5" />
                          <div>
                            <p className="font-medium text-white capitalize">{suggestion.type.replace('_', ' ')}</p>
                            <p className="text-sm text-slate-400 mt-1">{suggestion.suggestion}</p>
                          </div>
                        </div>
                      </div>
                    ))}

                    {(!pairingDashboard?.fatigue_alerts?.length && !pairingDashboard?.optimization_suggestions?.length) && (
                      <div className="text-center py-8 text-slate-500">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50 text-green-400" />
                        <p>{t('crew.noAlertsAtThisTime')}</p>
                        <p className="text-xs mt-1 text-green-400">{t('crew.allCrewPairingsHealthy')}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Pairings Table */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle>{t('crew.recentFlightPairings')}</CardTitle>
                <CardDescription>{t('crew.latestCrewPairingAssignments')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pairingDashboard?.recent_pairings?.slice(0, 10).map((pairing, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="text-sm">
                          <p className="text-white font-medium">{pairing.flight_number}</p>
                          <p className="text-slate-500 text-xs">{pairing.flight_date}</p>
                        </div>
                        <Separator orientation="vertical" className="h-8" />
                        <div>
                          <p className="text-slate-300">{pairing.captain_name} <span className="text-slate-500">+</span> {pairing.first_officer_name}</p>
                          <p className="text-xs text-slate-500">{pairing.departure_airport} → {pairing.arrival_airport}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-400">{pairing.block_time_hours}h</span>
                        {pairing.is_long_haul && (
                          <Badge className="bg-purple-500/20 text-purple-300 text-xs">{t('crew.longHaul')}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!pairingDashboard?.recent_pairings || pairingDashboard.recent_pairings.length === 0) && (
                    <div className="text-center py-8 text-slate-500">
                      <Plane className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{t('crew.noRecentPairings')}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI FATIGUE PREDICTION TAB */}
        <TabsContent value="fatigue-ai">
          <div className="space-y-6">
            {/* AI Header */}
            <Card className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 border-purple-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                      <Brain className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        AI-Powered Fatigue Prediction
                        <Sparkles className="w-5 h-5 text-yellow-400" />
                      </h2>
                      <p className="text-slate-400">Predicting fatigue risk 24-48 hours ahead using GPT-5.2 analysis</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchFatiguePredictions} disabled={loadingPredictions}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingPredictions ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button onClick={runFatigueAnalysis} disabled={loadingPredictions} className="bg-purple-500 hover:bg-purple-600">
                      {loadingPredictions ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                      Run AI Analysis
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Risk Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{fatiguePredictions?.total_crew_analyzed || 0}</p>
                    <p className="text-sm text-slate-400">Crew Analyzed</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-red-500/10 border-red-500/30">
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-400">{fatiguePredictions?.critical_risk_count || 0}</p>
                    <p className="text-sm text-red-300">Critical Risk</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-orange-500/10 border-orange-500/30">
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-orange-400">{fatiguePredictions?.high_risk_count || 0}</p>
                    <p className="text-sm text-orange-300">High Risk</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-yellow-400">{fatiguePredictions?.moderate_risk_count || 0}</p>
                    <p className="text-sm text-yellow-300">Moderate</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-400">{(fatiguePredictions?.low_risk_count || 0) + (fatiguePredictions?.minimal_risk_count || 0)}</p>
                    <p className="text-sm text-green-300">Low/Minimal</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Urgent Alerts */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    Urgent Alerts
                  </CardTitle>
                  <CardDescription>Crew requiring immediate attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {fatiguePredictions?.urgent_alerts?.slice(0, 5).map((alert, idx) => (
                      <div key={idx} className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-white">{alert.crew_name}</p>
                          <Badge className={alert.risk_level === 'critical' ? 'bg-red-500' : 'bg-orange-500'}>
                            {alert.risk_level}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-400">{alert.role}</span>
                          <span className="text-red-300">Score: {alert.fatigue_score}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Peak risk in {alert.peak_hours}h • {alert.recommended_action}
                        </p>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="w-full mt-2 text-xs"
                          onClick={() => viewCrewPrediction(alert.crew_id)}
                        >
                          <Eye className="w-3 h-3 mr-1" /> View Details
                        </Button>
                      </div>
                    ))}
                    {(!fatiguePredictions?.urgent_alerts || fatiguePredictions.urgent_alerts.length === 0) && (
                      <div className="text-center py-6 text-slate-500">
                        <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-400" />
                        <p className="text-green-400">No urgent alerts</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Predictions List */}
              <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChart className="w-5 h-5 text-purple-400" />
                    Crew Fatigue Predictions (48h)
                  </CardTitle>
                  <CardDescription>AI-generated fatigue risk forecast</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {fatiguePredictions?.predictions?.slice(0, 15).map((pred, idx) => (
                        <div 
                          key={idx} 
                          className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-purple-500/30 transition-colors cursor-pointer"
                          onClick={() => viewCrewPrediction(pred.crew_member_id)}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                pred.current_risk_level === 'critical' ? 'bg-red-500/20 text-red-400' :
                                pred.current_risk_level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                pred.current_risk_level === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-green-500/20 text-green-400'
                              }`}>
                                {pred.current_fatigue_score?.toFixed(0)}
                              </div>
                              <div>
                                <p className="font-medium text-white">{pred.crew_name}</p>
                                <p className="text-xs text-slate-500">{pred.role} • Peak risk in {pred.peak_risk_hours_ahead}h</p>
                              </div>
                            </div>
                            <Badge className={
                              pred.current_risk_level === 'critical' ? 'bg-red-500' :
                              pred.current_risk_level === 'high' ? 'bg-orange-500' :
                              pred.current_risk_level === 'moderate' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }>
                              {pred.current_risk_level}
                            </Badge>
                          </div>
                          
                          {/* Simple Timeline Visualization */}
                          <div className="flex items-center gap-1 mb-2">
                            {pred.predictions_timeline?.map((point, i) => (
                              <div key={i} className="flex-1">
                                <div 
                                  className={`h-2 rounded-full ${
                                    point.risk_level === 'critical' ? 'bg-red-500' :
                                    point.risk_level === 'high' ? 'bg-orange-500' :
                                    point.risk_level === 'moderate' ? 'bg-yellow-500' :
                                    'bg-green-500'
                                  }`}
                                  style={{ opacity: 0.3 + (point.predicted_score / 100) * 0.7 }}
                                />
                                <p className="text-[10px] text-slate-500 text-center mt-1">+{point.hours_ahead}h</p>
                              </div>
                            ))}
                          </div>
                          
                          {/* Factors */}
                          {pred.contributing_factors?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {pred.contributing_factors.slice(0, 3).map((f, i) => (
                                <Badge key={i} variant="outline" className="text-[10px]">
                                  {f.name || f.factor_type}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      {(!fatiguePredictions?.predictions || fatiguePredictions.predictions.length === 0) && (
                        <div className="text-center py-12 text-slate-500">
                          <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No predictions available</p>
                          <p className="text-xs mt-1">Click "Run AI Analysis" to generate predictions</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Scheduling Recommendations */}
            {fatiguePredictions?.scheduling_recommendations?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    AI Scheduling Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {fatiguePredictions.scheduling_recommendations.map((rec, idx) => (
                      <div key={idx} className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="font-medium text-white mb-1">{rec.crew_name}</p>
                        <p className="text-sm text-slate-400">{rec.recommendation}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ADD CREW MEMBER DIALOG - Enhanced Multi-Step */}
      <Dialog open={showCrewDialog} onOpenChange={(open) => {
        setShowCrewDialog(open);
        if (!open) resetCrewForm();
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Add Crew Member
            </DialogTitle>
            {/* Step Indicator */}
            <div className="flex items-center gap-2 pt-4">
              {[1, 2, 3, 4, 5, 6, 7].map(step => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    crewFormStep === step ? 'bg-blue-500 text-white' :
                    crewFormStep > step ? 'bg-green-500/20 text-green-400' :
                    'bg-slate-700 text-slate-400'
                  }`}>
                    {crewFormStep > step ? <CheckCircle2 className="w-4 h-4" /> : step}
                  </div>
                  {step < 7 && <div className={`w-6 h-0.5 ${crewFormStep > step ? 'bg-green-500/50' : 'bg-slate-700'}`} />}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {crewFormStep === 1 && 'Step 1: Basic Information'}
              {crewFormStep === 2 && 'Step 2: Personal Details'}
              {crewFormStep === 3 && 'Step 3: Passport & Visa'}
              {crewFormStep === 4 && 'Step 4: Employment Details'}
              {crewFormStep === 5 && 'Step 5: License & Medical'}
              {crewFormStep === 6 && 'Step 6: Contact & Emergency'}
              {crewFormStep === 7 && 'Step 7: Operational & Notes'}
            </p>
          </DialogHeader>
          
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4 py-4">
              {/* STEP 1: Basic Information */}
              {crewFormStep === 1 && (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <p className="text-sm text-blue-300"><User className="w-4 h-4 inline mr-2" />Basic identification and role assignment</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Employee ID *</Label>
                      <Input placeholder="e.g., CPT003" value={crewForm.employee_id} onChange={(e) => setCrewForm({...crewForm, employee_id: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Role *</Label>
                      <Select value={crewForm.role} onValueChange={(v) => setCrewForm({...crewForm, role: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="captain">Captain</SelectItem>
                          <SelectItem value="first_officer">First Officer</SelectItem>
                          <SelectItem value="second_officer">Second Officer</SelectItem>
                          <SelectItem value="purser">Purser</SelectItem>
                          <SelectItem value="cabin_crew">Cabin Crew</SelectItem>
                          <SelectItem value="instructor">Instructor</SelectItem>
                          <SelectItem value="examiner">Examiner</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input value={crewForm.first_name} onChange={(e) => setCrewForm({...crewForm, first_name: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      <Input value={crewForm.last_name} onChange={(e) => setCrewForm({...crewForm, last_name: e.target.value})} className="bg-slate-800" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={crewForm.email} onChange={(e) => setCrewForm({...crewForm, email: e.target.value})} className="bg-slate-800" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input value={crewForm.phone} onChange={(e) => setCrewForm({...crewForm, phone: e.target.value})} className="bg-slate-800" placeholder="+966 5x xxx xxxx" />
                    </div>
                    <div className="space-y-2">
                      <Label>Base Airport *</Label>
                      <Select value={crewForm.base_airport} onValueChange={(v) => setCrewForm({...crewForm, base_airport: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OEJN">OEJN - Jeddah</SelectItem>
                          <SelectItem value="OERK">OERK - Riyadh</SelectItem>
                          <SelectItem value="OEDF">OEDF - Dammam</SelectItem>
                          <SelectItem value="OEMA">OEMA - Madinah</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Personal Details */}
              {crewFormStep === 2 && (
                <div className="space-y-4">
                  <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <p className="text-sm text-purple-300"><Heart className="w-4 h-4 inline mr-2" />Personal identification details</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input type="date" value={crewForm.date_of_birth} onChange={(e) => setCrewForm({...crewForm, date_of_birth: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Gender</Label>
                      <Select value={crewForm.gender} onValueChange={(v) => setCrewForm({...crewForm, gender: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Nationality</Label>
                    <Select value={crewForm.nationality} onValueChange={(v) => setCrewForm({...crewForm, nationality: v})}>
                      <SelectTrigger className="bg-slate-800"><SelectValue placeholder="Select nationality" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Saudi">Saudi</SelectItem>
                        <SelectItem value="Emirati">Emirati</SelectItem>
                        <SelectItem value="Egyptian">Egyptian</SelectItem>
                        <SelectItem value="Jordanian">Jordanian</SelectItem>
                        <SelectItem value="Pakistani">Pakistani</SelectItem>
                        <SelectItem value="Indian">Indian</SelectItem>
                        <SelectItem value="British">British</SelectItem>
                        <SelectItem value="American">American</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* STEP 3: Passport & Visa */}
              {crewFormStep === 3 && (
                <div className="space-y-4">
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="text-sm text-green-300"><Globe className="w-4 h-4 inline mr-2" />Travel document information</p>
                  </div>
                  <h4 className="font-medium text-white">Passport Details</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Passport Number</Label>
                      <Input value={crewForm.passport_number} onChange={(e) => setCrewForm({...crewForm, passport_number: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Expiry Date</Label>
                      <Input type="date" value={crewForm.passport_expiry} onChange={(e) => setCrewForm({...crewForm, passport_expiry: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Issuing Country</Label>
                      <Input value={crewForm.passport_issuing_country} onChange={(e) => setCrewForm({...crewForm, passport_issuing_country: e.target.value})} className="bg-slate-800" placeholder="e.g., Saudi Arabia" />
                    </div>
                  </div>
                  <Separator className="bg-slate-700" />
                  <h4 className="font-medium text-white">Visa Details (if applicable)</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Visa Type</Label>
                      <Select value={crewForm.visa_type} onValueChange={(v) => setCrewForm({...crewForm, visa_type: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="work">Work Visa</SelectItem>
                          <SelectItem value="residence">Residence</SelectItem>
                          <SelectItem value="crew">Crew Visa</SelectItem>
                          <SelectItem value="multiple_entry">Multiple Entry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Visa Number</Label>
                      <Input value={crewForm.visa_number} onChange={(e) => setCrewForm({...crewForm, visa_number: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Visa Expiry</Label>
                      <Input type="date" value={crewForm.visa_expiry} onChange={(e) => setCrewForm({...crewForm, visa_expiry: e.target.value})} className="bg-slate-800" />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4: Employment Details */}
              {crewFormStep === 4 && (
                <div className="space-y-4">
                  <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                    <p className="text-sm text-orange-300"><Briefcase className="w-4 h-4 inline mr-2" />Employment and seniority information</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Hire Date</Label>
                      <Input type="date" value={crewForm.hire_date} onChange={(e) => setCrewForm({...crewForm, hire_date: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Seniority Number</Label>
                      <Input type="number" value={crewForm.seniority_number} onChange={(e) => setCrewForm({...crewForm, seniority_number: e.target.value})} className="bg-slate-800" placeholder="e.g., 125" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Employment Type</Label>
                      <Select value={crewForm.employment_type} onValueChange={(v) => setCrewForm({...crewForm, employment_type: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">Full-Time</SelectItem>
                          <SelectItem value="part_time">Part-Time</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="seasonal">Seasonal</SelectItem>
                          <SelectItem value="freelance">Freelance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Contract End Date</Label>
                      <Input type="date" value={crewForm.contract_end_date} onChange={(e) => setCrewForm({...crewForm, contract_end_date: e.target.value})} className="bg-slate-800" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Fleet Assignment</Label>
                    <Select value={crewForm.fleet_assignment} onValueChange={(v) => setCrewForm({...crewForm, fleet_assignment: v})}>
                      <SelectTrigger className="bg-slate-800"><SelectValue placeholder="Select fleet" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="narrow_body">Narrow Body (A320/B737)</SelectItem>
                        <SelectItem value="wide_body">Wide Body (B777/B787/A350)</SelectItem>
                        <SelectItem value="regional">Regional (ATR/E190)</SelectItem>
                        <SelectItem value="cargo">Cargo Fleet</SelectItem>
                        <SelectItem value="mixed">Mixed Fleet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* STEP 5: License & Medical */}
              {crewFormStep === 5 && (
                <div className="space-y-4">
                  <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                    <p className="text-sm text-cyan-300"><CreditCard className="w-4 h-4 inline mr-2" />License and medical certifications</p>
                  </div>
                  <h4 className="font-medium text-white">License Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>License Type</Label>
                      <Select value={crewForm.license_type} onValueChange={(v) => setCrewForm({...crewForm, license_type: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None (Cabin Crew)</SelectItem>
                          <SelectItem value="atpl">ATPL</SelectItem>
                          <SelectItem value="cpl">CPL</SelectItem>
                          <SelectItem value="mpl">MPL</SelectItem>
                          <SelectItem value="ppl">PPL</SelectItem>
                          <SelectItem value="cabin_crew">Cabin Crew Certificate</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>License Number</Label>
                      <Input value={crewForm.license_number} onChange={(e) => setCrewForm({...crewForm, license_number: e.target.value})} className="bg-slate-800" placeholder="e.g., SAU-ATPL-12345" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Issuing Authority</Label>
                      <Select value={crewForm.license_issuing_authority} onValueChange={(v) => setCrewForm({...crewForm, license_issuing_authority: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="GACA">GACA (Saudi)</SelectItem>
                          <SelectItem value="EASA">EASA (Europe)</SelectItem>
                          <SelectItem value="FAA">FAA (USA)</SelectItem>
                          <SelectItem value="GCAA">GCAA (UAE)</SelectItem>
                          <SelectItem value="PCAA">PCAA (Pakistan)</SelectItem>
                          <SelectItem value="DGCA">DGCA (India)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>License Expiry</Label>
                      <Input type="date" value={crewForm.license_expiry} onChange={(e) => setCrewForm({...crewForm, license_expiry: e.target.value})} className="bg-slate-800" />
                    </div>
                  </div>
                  <Separator className="bg-slate-700" />
                  <h4 className="font-medium text-white">Medical Certificate</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Medical Class</Label>
                      <Select value={crewForm.medical_class} onValueChange={(v) => setCrewForm({...crewForm, medical_class: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="class_1">Class 1 (Flight Crew)</SelectItem>
                          <SelectItem value="class_2">Class 2</SelectItem>
                          <SelectItem value="class_3">Class 3 (Cabin Crew)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Medical Expiry</Label>
                      <Input type="date" value={crewForm.medical_expiry} onChange={(e) => setCrewForm({...crewForm, medical_expiry: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Restrictions</Label>
                      <Input value={crewForm.medical_restrictions} onChange={(e) => setCrewForm({...crewForm, medical_restrictions: e.target.value})} className="bg-slate-800" placeholder="e.g., VDL (glasses)" />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 6: Contact & Emergency */}
              {crewFormStep === 6 && (
                <div className="space-y-4">
                  <div className="p-3 bg-pink-500/10 rounded-lg border border-pink-500/30">
                    <p className="text-sm text-pink-300"><Home className="w-4 h-4 inline mr-2" />Address and emergency contacts</p>
                  </div>
                  <h4 className="font-medium text-white">Address</h4>
                  <div className="space-y-2">
                    <Label>Address Line 1</Label>
                    <Input value={crewForm.address_line1} onChange={(e) => setCrewForm({...crewForm, address_line1: e.target.value})} className="bg-slate-800" />
                  </div>
                  <div className="space-y-2">
                    <Label>Address Line 2</Label>
                    <Input value={crewForm.address_line2} onChange={(e) => setCrewForm({...crewForm, address_line2: e.target.value})} className="bg-slate-800" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input value={crewForm.city} onChange={(e) => setCrewForm({...crewForm, city: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Input value={crewForm.country} onChange={(e) => setCrewForm({...crewForm, country: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Postal Code</Label>
                      <Input value={crewForm.postal_code} onChange={(e) => setCrewForm({...crewForm, postal_code: e.target.value})} className="bg-slate-800" />
                    </div>
                  </div>
                  <Separator className="bg-slate-700" />
                  <h4 className="font-medium text-white">Emergency Contact</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Contact Name</Label>
                      <Input value={crewForm.emergency_contact_name} onChange={(e) => setCrewForm({...crewForm, emergency_contact_name: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input value={crewForm.emergency_contact_phone} onChange={(e) => setCrewForm({...crewForm, emergency_contact_phone: e.target.value})} className="bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <Label>Relationship</Label>
                      <Select value={crewForm.emergency_contact_relationship} onValueChange={(v) => setCrewForm({...crewForm, emergency_contact_relationship: v})}>
                        <SelectTrigger className="bg-slate-800"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="spouse">Spouse</SelectItem>
                          <SelectItem value="parent">Parent</SelectItem>
                          <SelectItem value="sibling">Sibling</SelectItem>
                          <SelectItem value="child">Child</SelectItem>
                          <SelectItem value="friend">Friend</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 7: Operational & Notes */}
              {crewFormStep === 7 && (
                <div className="space-y-4">
                  <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <p className="text-sm text-yellow-300"><UserCog className="w-4 h-4 inline mr-2" />Operational qualifications and notes</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Languages Spoken</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Arabic', 'English', 'French', 'German', 'Spanish', 'Urdu', 'Hindi', 'Tagalog', 'Mandarin'].map(lang => (
                        <Badge
                          key={lang}
                          variant={crewForm.languages_spoken.includes(lang) ? 'default' : 'outline'}
                          className={`cursor-pointer ${crewForm.languages_spoken.includes(lang) ? 'bg-blue-500' : ''}`}
                          onClick={() => {
                            const langs = crewForm.languages_spoken.includes(lang)
                              ? crewForm.languages_spoken.filter(l => l !== lang)
                              : [...crewForm.languages_spoken, lang];
                            setCrewForm({...crewForm, languages_spoken: langs});
                          }}
                        >
                          {lang}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Aircraft Type Ratings</Label>
                    <div className="flex flex-wrap gap-2">
                      {['B737', 'B777', 'B787', 'A320', 'A330', 'A350', 'A380', 'E190', 'ATR72'].map(type => (
                        <Badge
                          key={type}
                          variant={crewForm.aircraft_type_ratings.includes(type) ? 'default' : 'outline'}
                          className={`cursor-pointer ${crewForm.aircraft_type_ratings.includes(type) ? 'bg-purple-500' : ''}`}
                          onClick={() => {
                            const types = crewForm.aircraft_type_ratings.includes(type)
                              ? crewForm.aircraft_type_ratings.filter(t => t !== type)
                              : [...crewForm.aircraft_type_ratings, type];
                            setCrewForm({...crewForm, aircraft_type_ratings: types});
                          }}
                        >
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Special Qualifications</Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        {id: 'instructor', label: 'Instructor'},
                        {id: 'examiner', label: 'Examiner'},
                        {id: 'line_training_captain', label: 'Line Training Captain'},
                        {id: 'check_captain', label: 'Check Captain'},
                        {id: 'safety_pilot', label: 'Safety Pilot'}
                      ].map(qual => (
                        <Badge
                          key={qual.id}
                          variant={crewForm.special_qualifications.includes(qual.id) ? 'default' : 'outline'}
                          className={`cursor-pointer ${crewForm.special_qualifications.includes(qual.id) ? 'bg-green-500' : ''}`}
                          onClick={() => {
                            const quals = crewForm.special_qualifications.includes(qual.id)
                              ? crewForm.special_qualifications.filter(q => q !== qual.id)
                              : [...crewForm.special_qualifications, qual.id];
                            setCrewForm({...crewForm, special_qualifications: quals});
                          }}
                        >
                          {qual.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes / Comments</Label>
                    <Textarea
                      value={crewForm.notes}
                      onChange={(e) => setCrewForm({...crewForm, notes: e.target.value})}
                      className="bg-slate-800 min-h-[100px]"
                      placeholder="Any additional notes about this crew member..."
                    />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter className="flex justify-between">
            <div>
              {crewFormStep > 1 && (
                <Button variant="outline" onClick={() => setCrewFormStep(crewFormStep - 1)}>
                  <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCrewDialog(false)}>Cancel</Button>
              {crewFormStep < 7 ? (
                <Button 
                  onClick={() => setCrewFormStep(crewFormStep + 1)}
                  disabled={crewFormStep === 1 && (!crewForm.employee_id || !crewForm.first_name || !crewForm.last_name || !crewForm.email)}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  Next <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={createCrewMember}
                  disabled={!crewForm.employee_id || !crewForm.first_name || !crewForm.last_name || !crewForm.email}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <Save className="w-4 h-4 mr-2" /> Create Crew Member
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD QUALIFICATION DIALOG */}
      <Dialog open={showQualDialog} onOpenChange={setShowQualDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-cyan-400" />
              Add Qualification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Crew Member</Label>
              <Select value={qualForm.crew_member_id} onValueChange={(v) => setQualForm({...qualForm, crew_member_id: v})}>
                <SelectTrigger className="bg-slate-800">
                  <SelectValue placeholder="Select crew member" />
                </SelectTrigger>
                <SelectContent>
                  {crewMembers.map(crew => (
                    <SelectItem key={crew.id} value={crew.id}>
                      {crew.full_name || `${crew.first_name} ${crew.last_name}`} ({crew.employee_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={qualForm.qualification_type} onValueChange={(v) => setQualForm({...qualForm, qualification_type: v})}>
                  <SelectTrigger className="bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="license">License (ATPL/CPL)</SelectItem>
                    <SelectItem value="type_rating">Type Rating</SelectItem>
                    <SelectItem value="medical">Medical</SelectItem>
                    <SelectItem value="recurrent">Recurrent Training</SelectItem>
                    <SelectItem value="line_check">Line Check</SelectItem>
                    <SelectItem value="proficiency_check">Proficiency Check</SelectItem>
                    <SelectItem value="dangerous_goods">Dangerous Goods</SelectItem>
                    <SelectItem value="crm">CRM</SelectItem>
                    <SelectItem value="security">Security</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Issued By</Label>
                <Select value={qualForm.issued_by} onValueChange={(v) => setQualForm({...qualForm, issued_by: v})}>
                  <SelectTrigger className="bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GACA">GACA</SelectItem>
                    <SelectItem value="EASA">EASA</SelectItem>
                    <SelectItem value="FAA">FAA</SelectItem>
                    <SelectItem value="Company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Qualification Name</Label>
              <Input
                placeholder="e.g., ATPL, B737 Type Rating"
                value={qualForm.name}
                onChange={(e) => setQualForm({...qualForm, name: e.target.value})}
                className="bg-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label>License/Certificate Number</Label>
              <Input
                placeholder="e.g., SAU-12345"
                value={qualForm.number}
                onChange={(e) => setQualForm({...qualForm, number: e.target.value})}
                className="bg-slate-800"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={qualForm.issue_date}
                  onChange={(e) => setQualForm({...qualForm, issue_date: e.target.value})}
                  className="bg-slate-800"
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={qualForm.expiry_date}
                  onChange={(e) => setQualForm({...qualForm, expiry_date: e.target.value})}
                  className="bg-slate-800"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQualDialog(false)}>Cancel</Button>
            <Button 
              onClick={createQualification} 
              disabled={!qualForm.crew_member_id || !qualForm.name || !qualForm.issue_date}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Qualification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CREW DETAIL DIALOG */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Crew Member Details
            </DialogTitle>
          </DialogHeader>
          {selectedCrew && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center">
                  <User className="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedCrew.crew_member.full_name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={getRoleBadge(selectedCrew.crew_member.role)}>
                      {selectedCrew.crew_member.role?.replace('_', ' ')}
                    </Badge>
                    <Badge className={getStatusBadge(selectedCrew.crew_member.status)}>
                      {selectedCrew.crew_member.status}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Employee ID</p>
                  <p className="text-white">{selectedCrew.crew_member.employee_id}</p>
                </div>
                <div>
                  <p className="text-slate-500">Base Airport</p>
                  <p className="text-white">{selectedCrew.crew_member.base_airport}</p>
                </div>
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="text-white">{selectedCrew.crew_member.email}</p>
                </div>
                <div>
                  <p className="text-slate-500">Phone</p>
                  <p className="text-white">{selectedCrew.crew_member.phone || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-white mb-3">Qualifications ({selectedCrew.qualifications.length})</h3>
                <div className="space-y-2">
                  {selectedCrew.qualifications.map(q => (
                    <div key={q.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                      <span className="text-slate-300">{q.name}</span>
                      <span className={`text-sm ${q.days_until_expiry <= 30 ? 'text-yellow-400' : 'text-green-400'}`}>
                        {q.days_until_expiry} days
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* FATIGUE PREDICTION DETAIL DIALOG */}
      <Dialog open={showPredictionDialog} onOpenChange={setShowPredictionDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              AI Fatigue Prediction Details
            </DialogTitle>
          </DialogHeader>
          {selectedPrediction && (
            <div className="space-y-6 py-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold ${
                    selectedPrediction.current_risk_level === 'critical' ? 'bg-red-500/20 text-red-400' :
                    selectedPrediction.current_risk_level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                    selectedPrediction.current_risk_level === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {selectedPrediction.current_fatigue_score?.toFixed(0)}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedPrediction.crew_name}</h2>
                    <p className="text-slate-400">{selectedPrediction.role}</p>
                  </div>
                </div>
                <Badge className={`text-lg px-4 py-2 ${
                  selectedPrediction.current_risk_level === 'critical' ? 'bg-red-500' :
                  selectedPrediction.current_risk_level === 'high' ? 'bg-orange-500' :
                  selectedPrediction.current_risk_level === 'moderate' ? 'bg-yellow-500' :
                  'bg-green-500'
                }`}>
                  {selectedPrediction.current_risk_level?.toUpperCase()}
                </Badge>
              </div>

              {/* AI Analysis */}
              <Card className="bg-purple-500/10 border-purple-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-purple-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-purple-300 mb-1">AI Analysis</h4>
                      <p className="text-slate-300">{selectedPrediction.ai_analysis}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 48h Prediction Timeline */}
              <div>
                <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                  <LineChart className="w-4 h-4 text-blue-400" />
                  48-Hour Fatigue Forecast
                </h3>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-end justify-between gap-2 h-32">
                    {selectedPrediction.predictions_timeline?.map((point, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div 
                          className={`w-full rounded-t transition-all ${
                            point.risk_level === 'critical' ? 'bg-red-500' :
                            point.risk_level === 'high' ? 'bg-orange-500' :
                            point.risk_level === 'moderate' ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ height: `${point.predicted_score}%` }}
                        />
                        <p className="text-xs text-slate-500 mt-2">+{point.hours_ahead}h</p>
                        <p className="text-xs font-medium text-white">{point.predicted_score?.toFixed(0)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-slate-500">
                    <span>Now</span>
                    <span>Peak: +{selectedPrediction.peak_risk_hours_ahead}h ({selectedPrediction.peak_risk_score?.toFixed(0)})</span>
                    <span>+48h</span>
                  </div>
                </div>
              </div>

              {/* Contributing Factors */}
              <div>
                <h3 className="font-medium text-white mb-3">Contributing Factors</h3>
                <div className="space-y-2">
                  {selectedPrediction.contributing_factors?.map((factor, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          factor.severity === 'high' ? 'bg-red-500' :
                          factor.severity === 'medium' ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`} />
                        <div>
                          <p className="text-white">{factor.name || factor.factor_type}</p>
                          <p className="text-xs text-slate-500">{factor.description}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={
                        factor.severity === 'high' ? 'border-red-500 text-red-400' :
                        factor.severity === 'medium' ? 'border-yellow-500 text-yellow-400' :
                        'border-green-500 text-green-400'
                      }>
                        +{factor.score_impact?.toFixed(0)} pts
                      </Badge>
                    </div>
                  ))}
                  {(!selectedPrediction.contributing_factors || selectedPrediction.contributing_factors.length === 0) && (
                    <p className="text-slate-500 text-sm">No significant contributing factors identified</p>
                  )}
                </div>
              </div>

              {/* Duty Context */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4">
                    <p className="text-slate-400 text-sm">Duty Hours (7 days)</p>
                    <p className="text-2xl font-bold text-white">{selectedPrediction.recent_duty_hours_7_days?.toFixed(1)}h</p>
                    <p className="text-xs text-slate-500">Limit: 60h</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4">
                    <p className="text-slate-400 text-sm">Last Rest</p>
                    <p className="text-2xl font-bold text-white">{selectedPrediction.last_rest_hours?.toFixed(1)}h</p>
                    <p className="text-xs text-slate-500">Min required: 10h</p>
                  </CardContent>
                </Card>
              </div>

              {/* AI Recommendations */}
              <div>
                <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  AI Recommendations
                </h3>
                <div className="space-y-2">
                  {selectedPrediction.ai_recommendations?.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-blue-400 mt-0.5" />
                      <p className="text-slate-300">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Metadata */}
              <div className="text-xs text-slate-500 border-t border-slate-700 pt-4">
                <p>Model: {selectedPrediction.model_version} • Generated: {new Date(selectedPrediction.prediction_generated_at).toLocaleString()}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
